/* eslint-disable react/prop-types */
import { isAuth } from "@/network/helper";
import {
  toggleInviteModal,
  toggleShoppingCartModal,
  toggleTaskEdit,
} from "@/store/common";
import { toggleModal } from "@/store/task";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./layoutStyle.module.scss";

import DODO_STATUS_TYPE from "@/constants/dodoStatusType";
import LICENSE_STATUS from "@/constants/licenseStatus";
import { ROLE } from "@/middleware.constant";
import { SidebarProvider } from "../ui/sidebar";
import AppSidebar from "./appSidebar";
import {
  INACTIVE_NOTICE_EXCLUDED_PATHS,
  noSideBarsMenuPages,
} from "./constant";
import useSub from "./useSub";
import useUser from "./useUser";
import TrialPeriodBanner from "./trialPeriodBanner";
import InactiveLicenseAlert from "./inactiveLicenseAlert";


const TaskDetailsModal = dynamic(
  () => import("../organisms/taskDetailsModal"),
  {
    ssr: false,
  },
);
const InviteMemberModal = dynamic(
  () => import("../organisms/inviteMemberModal"),
  {
    ssr: false,
  },
);
const ShoppingCart = dynamic(() => import("../organisms/shoppingCart/index"), {
  ssr: false,
});
const TourGuideModal = dynamic(() => import("../atoms/modal/tourGuideModal"), {
  ssr: false,
});
const MobileVersionWarningModal = dynamic(
  () => import("../atoms/modal/mobileVersionWarning"),
  {
    ssr: false,
  },
);

const requiredActiveLicense = (path) =>
  !INACTIVE_NOTICE_EXCLUDED_PATHS.some((item) => path.startsWith(item));

const isBlockedLicense = (license) =>
  license == LICENSE_STATUS.DORMANT || license == LICENSE_STATUS.INACTIVE;

const Layout = ({ children }) => {
  const router = useRouter();
  const [showSideMenu, setShowSideMenu] = useState(true);
  // const [showHeader, setShowHeader] = useState(false);
  const task = useSelector((state) => state?.task);
  const InviteModal = useSelector((state) => state?.common);
  const dispatch = useDispatch();

  const shoppingCartModal = useSelector(
    (state) => state?.common.shoppingCartModal,
  );

  const [licenseStatus, setLicenseStatus] = useState("");
  const [isSubActive, setIsSubActive] = useState(null);

  const handleClose = () => {
    dispatch(toggleModal());
  };

  const MemoizedTaskDetailsModal = useMemo(() => {
    return (
      <TaskDetailsModal
        open={task?.isTaskModalIsOpen}
        handleClose={handleClose}
      />
    );
  }, [task?.isTaskModalIsOpen]);

  const MemoizedInviteModal = useMemo(() => {
    return (
      <InviteMemberModal
        isOpen={InviteModal?.isInviteModalIsOpen}
        handleClose={() => dispatch(toggleInviteModal())}
      />
    );
  }, [InviteModal?.isInviteModalIsOpen]);

  const MemoizedShoppingCartModal = useMemo(() => {
    return (
      <ShoppingCart
        open={shoppingCartModal}
        handleClose={() => dispatch(toggleShoppingCartModal())}
      />
    );
  }, [shoppingCartModal]);

  useEffect(() => {
    const isPublicRoute = noSideBarsMenuPages.includes(router.pathname);
    setShowSideMenu(!isPublicRoute);
    dispatch(toggleTaskEdit(false));
  }, [router.pathname]);

  const { userData } = useUser(isAuth());

  const isAdministrator =
    userData &&
    (userData?.attributes?.role_numeric == ROLE.ADMIN ||
      userData?.attributes?.role_numeric == ROLE.OWNER);

  const isAllowedToFetchSubscription =
    isAuth() &&
    router.pathname != "/organization" &&
    router.pathname != "/on-board";

  const { subscriptionData, isSubFetching } = useSub(
    isAllowedToFetchSubscription == true,
  );

  useEffect(() => {
    if (subscriptionData) {
      const dodoStatus = subscriptionData?.attributes?.dodo_status;

      if (
        dodoStatus == DODO_STATUS_TYPE.active ||
        dodoStatus == DODO_STATUS_TYPE.trial
      ) {
        setIsSubActive(true);
      } else {
        setIsSubActive(false);
      }
    }
    if (
      !isSubFetching &&
      isAllowedToFetchSubscription &&
      subscriptionData?.status == 404
    ) {
      setIsSubActive(false);
    }
  }, [subscriptionData, isSubFetching, isAllowedToFetchSubscription]);

  useEffect(() => {
    if (isAuth() && userData) {
      let tempLicenseStatus = -1;

      switch (userData?.attributes?.license_status) {
        case LICENSE_STATUS.FULL_ACCESS:
          tempLicenseStatus = LICENSE_STATUS.FULL_ACCESS;
          break;
        case LICENSE_STATUS.VIEW_ONLY:
          tempLicenseStatus = LICENSE_STATUS.VIEW_ONLY;
          break;
        case LICENSE_STATUS.DORMANT:
          tempLicenseStatus = LICENSE_STATUS.DORMANT;
          break;
        case LICENSE_STATUS.INACTIVE:
          tempLicenseStatus = LICENSE_STATUS.INACTIVE;
          break;
      }
      setLicenseStatus(tempLicenseStatus);
    }
  }, [userData]);

  if (!navigator.onLine)
    return (
      <div>
        <p className="fs-14 text-[var(--text-300)] font-bold">
          {" "}
          No Internet Connection...{" "}
        </p>
      </div>
    );

  // Redirect them to subscription if they do not have any subscription.
  if (licenseStatus != "" && requiredActiveLicense(router.pathname)) {
    if (isAdministrator && isSubActive === false) router.push("/subscription");
    if (!isAdministrator && isBlockedLicense(licenseStatus))
      router.push("/subscription");
  }

  return (
    <>
      <SidebarProvider>
        {isAuth() && showSideMenu && <AppSidebar />}
        <main className={`${styles.layout_container} `}>
          <Head>
            <title>
              {isAuth()
                ? "Maaran AI - Marketing Orchestrator"
                : "Welcome to Maaran AI"}
            </title>
            <meta
              name="description"
              content="Maaran AI - a platform built for marketers by marketers. Simplify your marketing workflows effortlessly. "
            />
          </Head>

          <div className={`${styles.AnonymousLayout_children} w-full `}>
            <InactiveLicenseAlert licenseStatus={licenseStatus} />
            <TrialPeriodBanner />

            {children}

            {isAuth() && task?.isTaskModalIsOpen && MemoizedTaskDetailsModal}
            {isAuth() &&
              InviteModal?.isInviteModalIsOpen &&
              MemoizedInviteModal}
            {isAuth() && shoppingCartModal && MemoizedShoppingCartModal}

            <TourGuideModal />
            <MobileVersionWarningModal />
          </div>
        </main>
      </SidebarProvider>
    </>
  );
};

export default Layout;
